import { PropertyAmenity } from "@/enums/property.enum";
import { CheckIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import {
  AirConditioningIcon,
  AirportIcon,
  BabySitterReccomendationIcon,
  BalconyIcon,
  BarbequeIcon,
  BasementIcon,
  BeachfrontIcon,
  BuiltInWardrobesIcon,
  CableTvIcon,
  CarpetsIcon,
  CityViewIcon,
  CloseToMetroIcon,
  ConciergeIcon,
  ConferenceRoomIcon,
  DineInIcon,
  EquestrianIcon,
  GarageIcon,
  GardenViewIcon,
  GatedCommunityIcon,
  GazeboIcon,
  GolfViewIcon,
  HeatingIcon,
  HighFloorIcon,
  HospitalIcon,
  HotelIcon,
  JacuzziIcon,
  KidsPlaygroundIcon,
  KidsPoolIcon,
  KitchenAppliancesIcon,
  LakeViewIcon,
  LandmarkViewIcon,
  LaundryRoomIcon,
  MaidRoomIcon,
  MallIcon,
  MarbleFloorsIcon,
  MetroIcon,
  MidFloorIcon,
  MosqueIcon,
  OfficeIcon,
  OnLowFloorIcon,
  PantryIcon,
  ParkIcon,
  ParkingIcon,
  PetsAllowedIcon,
  PoolIcon,
  PrivacyIcon,
  PrivateGardenIcon,
  PrivateGymIcon,
  PrivateJacuzziIcon,
  PrivatePoolIcon,
  RenovatedIcon,
  RetailIcon,
  SaunaIcon,
  SeaViewIcon,
  SharedGymIcon,
  SharedPoolIcon,
  ShellAndCoreIcon,
  SpaIcon,
  SteamRoomIcon,
  StorageRoomIcon,
  StudyRoomIcon,
  TerraceIcon,
  ValetIcon,
  VastuCompliantIcon,
  VetIcon,
  WalkInClosetIcon,
  WaterfrontIcon,
  WineCellarIcon,
  WoodenFloorsIcon,
} from "../Icons";
import { P } from "./Heading";

type IconProps = {
  width?: number;
  height?: number;
  size?: number;
  strokeWidth: number;
};

const amenityIcons: Record<PropertyAmenity, React.ComponentType<IconProps>> = {
  beachfront: BeachfrontIcon,
  "golf-view": GolfViewIcon,
  "pets-allowed": PetsAllowedIcon,
  "shared-gym": SharedGymIcon,
  "landmark-view": LandmarkViewIcon,
  "private-garden": PrivateGardenIcon,
  "private-jacuzzi": PrivateJacuzziIcon,
  "shared-pool": SharedPoolIcon,
  "private-gym": PrivateGymIcon,
  "private-pool": PrivatePoolIcon,
  "waterfront-view": WaterfrontIcon,
  "wine-cellar": WineCellarIcon,
  "maids-room": MaidRoomIcon,
  study: StudyRoomIcon,
  "central-air-conditioning": AirConditioningIcon,
  balcony: BalconyIcon,
  security: PrivacyIcon,
  "built-in-wardrobes": BuiltInWardrobesIcon,
  "walk-in-closet": WalkInClosetIcon,
  "kitchen-appliances": KitchenAppliancesIcon,
  "view-of-landmark": LandmarkViewIcon,
  "shared-spa": SpaIcon,
  "gated-community": GatedCommunityIcon,
  "conference-room": ConferenceRoomIcon,
  "bbq-area": BarbequeIcon,
  "south-orientation": VastuCompliantIcon,
  "wood-flooring": WoodenFloorsIcon,
  "near-public-transportation": CloseToMetroIcon,
  "indoor-swimming-pool": PoolIcon,
  jacuzzi: JacuzziIcon,
  "maid-service": MaidRoomIcon,
  "near-supermarket": RetailIcon,
  "near-school": StudyRoomIcon,
  "east-orientation": VastuCompliantIcon,
  "laundry-room": LaundryRoomIcon,
  "private-garage": GarageIcon,
  "steam-room": SteamRoomIcon,
  "fully-fitted-kitchen": KitchenAppliancesIcon,
  "basement-parking": ParkingIcon,
  maintenance: RenovatedIcon,
  "near-golf": GolfViewIcon,
  "seawater-view": SeaViewIcon,
  "on-mid-floor": MidFloorIcon,
  "public-parks": ParkIcon,
  "core-and-shell": ShellAndCoreIcon,
  equestrian: EquestrianIcon,
  "garden-view": GardenViewIcon,
  "near-veterinary": VetIcon,
  heating: HeatingIcon,
  "beach-access": BeachfrontIcon,
  "near-airport": AirportIcon,
  "near-mall": MallIcon,
  "childrens-pool": KidsPoolIcon,
  "childrens-play-area": KidsPlaygroundIcon,
  "near-restaurants": DineInIcon,
  "near-metro": MetroIcon,
  "near-mosque": MosqueIcon,
  basement: BasementIcon,
  "near-hospital": HospitalIcon,
  "community-view": CityViewIcon,
  pantry: PantryIcon,
  "lake-view": LakeViewIcon,
  "upgraded-interior": RenovatedIcon,
  "west-orientation": VastuCompliantIcon,
  "within-a-compound": GatedCommunityIcon,
  "cable-ready": CableTvIcon,
  "city-view": CityViewIcon,
  sauna: SaunaIcon,
  "on-high-floor": HighFloorIcon,
  "storage-room": StorageRoomIcon,
  "marble-floors": MarbleFloorsIcon,
  lobby: ConciergeIcon,
  "vastu-compliant": VastuCompliantIcon,
  "near-hotel": HotelIcon,
  "drivers-room": ValetIcon,
  terrace: TerraceIcon,
  "covered-parking": GarageIcon,
  "concierge-service": ConciergeIcon,
  carpets: CarpetsIcon,
  gazebo: GazeboIcon,
  "cleaning-services": MaidRoomIcon,
  "visitors-parking": BasementIcon,
  "reception-area": ConciergeIcon,
  "office-partitions": OfficeIcon,
  "on-low-floor": OnLowFloorIcon,
  "north-orientation": VastuCompliantIcon,
  "children-daycare": BabySitterReccomendationIcon,
  "ground-floor": OnLowFloorIcon,
  "server-room": OfficeIcon,
};

export const Amenity = ({ amenity }: { amenity: PropertyAmenity }) => {
  const IconComponent = amenityIcons[amenity] || CheckIcon;

  const t = useTranslations("shared.amenities");
  const translatedName = t(amenity);

  return (
    <div className="flex items-center gap-1.5">
      <IconComponent strokeWidth={1} size={20} />
      <P className="text:xs sm:text-sm text-gray-800">
        {translatedName.includes(".") ? amenity : translatedName}
      </P>
    </div>
  );
};
